import { Link } from "react-router-dom";
import { Mail, Phone, Facebook } from "lucide-react";
import { useState } from "react";
import apiClient from "@/lib/api";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await apiClient.post("/newsletter", { email });
      toast.success("Subscribed. Thank you!");
      setEmail("");
    } catch (err) {
      toast.error("Could not subscribe. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <footer data-testid="site-footer" className="bg-[#29324A] text-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div>
            <div className="mb-5 leading-tight">
              <div className="font-display text-2xl text-white tracking-tight">Shamima Yasmin Anwar</div>
              <div className="text-[10px] font-semibold tracking-[0.1em] text-slate-400 uppercase mt-1.5">MARN 2619415 | IAA Licence No. 202603837</div>
            </div>
            <p className="text-sm leading-relaxed text-slate-300">
              Confidential, ethical and personalised Australian and New Zealand immigration guidance for students, skilled professionals, families and businesses.
            </p>

            {/* Social */}
            <div className="flex items-center gap-3 mt-6">
              <a href="https://www.facebook.com/profile.php?id=61589771857650" target="_blank" rel="noopener noreferrer" data-testid="footer-fb" className="h-9 w-9 border border-white/20 flex items-center justify-center text-slate-300 hover:text-white hover:bg-[#C43A89] hover:border-[#C43A89] transition-colors" aria-label="Shamima Yasmin Anwar on Facebook">
                <Facebook className="h-4 w-4" />
              </a>
            </div>
          </div>

          <div>
            <div className="text-[11px] font-bold tracking-[0.18em] text-[#C43A89] uppercase mb-4">
              Navigate
            </div>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/services" className="hover:text-[#C43A89] transition-colors">Migration Services</Link></li>
              <li><Link to="/services/visitor" className="hover:text-[#C43A89] transition-colors">Visitor Visa (600)</Link></li>
              <li><Link to="/service-charges" data-testid="footer-link-pricing" className="hover:text-[#C43A89] transition-colors">Pricing & Service Charges</Link></li>
              <li><Link to="/visa-statement-builder" data-testid="footer-link-gs-builder" className="hover:text-[#C43A89] transition-colors text-[#C43A89] font-semibold">GS &amp; SOP Statement Builder</Link></li>
              <li><Link to="/pr-assessment" className="hover:text-[#C43A89] transition-colors">Free PR Assessment</Link></li>
              <li><Link to="/points-calculator" className="hover:text-[#C43A89] transition-colors">Points Calculator</Link></li>
              <li><Link to="/book-consultation" className="hover:text-[#C43A89] transition-colors">Book Consultation</Link></li>
              <li><Link to="/resources" className="hover:text-[#C43A89] transition-colors">Resources & Blog</Link></li>
              <li><Link to="/contact" className="hover:text-[#C43A89] transition-colors">Contact</Link></li>
            </ul>
          </div>

          <div>
            <div className="text-[11px] font-bold tracking-[0.18em] text-[#C43A89] uppercase mb-4">
              Contact
            </div>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2.5">
                <Mail className="h-4 w-4 mt-0.5 text-[#C43A89] shrink-0" />
                <a href="mailto:yasmin@studynet.com.au" className="hover:text-[#C43A89] break-all">yasmin@studynet.com.au</a>
              </li>
              <li className="flex items-start gap-2.5">
                <Phone className="h-4 w-4 mt-0.5 text-[#C43A89] shrink-0" />
                <span>By appointment</span>
              </li>
              <li>
                <Link to="/book-consultation" data-testid="footer-book-consultation" className="inline-flex items-center justify-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] text-white text-[11px] font-bold tracking-[0.14em] uppercase px-4 py-2.5 transition-colors w-full">
                  Book a Consultation
                </Link>
              </li>
              <li>
                <button type="button" data-testid="footer-request-callback" onClick={() => window.dispatchEvent(new CustomEvent("open-callback"))}
                  className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-[#C43A89] hover:text-[#C43A89] text-white text-[11px] font-bold tracking-[0.14em] uppercase px-4 py-2.5 transition-colors w-full">
                  Request a Callback
                </button>
              </li>
            </ul>
            <div className="flex items-center gap-3 mt-5 lg:hidden">
              <a href="https://www.facebook.com/profile.php?id=61589771857650" target="_blank" rel="noopener noreferrer" className="text-slate-300 hover:text-[#C43A89]" aria-label="Facebook"><Facebook className="h-5 w-5" /></a>
            </div>
          </div>

          <div>
            <div className="text-[11px] font-bold tracking-[0.18em] text-[#C43A89] uppercase mb-4">
              Stay Updated
            </div>
            <p className="text-sm text-slate-300 mb-4">
              Subscribe for visa updates, invitation rounds and policy changes.
            </p>
            <form onSubmit={subscribe} className="space-y-3">
              <input
                data-testid="newsletter-email-input"
                type="email"
                required
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white/5 border border-white/20 px-4 py-2.5 text-sm text-white placeholder:text-slate-400 focus:border-[#C43A89] focus:outline-none"
              />
              <button
                data-testid="newsletter-submit-btn"
                disabled={loading}
                className="w-full bg-[#C43A89] hover:bg-[#a22b6e] disabled:opacity-50 text-white text-sm font-semibold py-2.5 transition-colors"
              >
                {loading ? "Subscribing..." : "Subscribe"}
              </button>
            </form>
          </div>
        </div>

        <div className="brand-rule my-10 opacity-40" />

        <div className="text-xs text-slate-400 space-y-3 max-w-4xl">
          <p>
            <strong className="text-slate-200">Disclaimer:</strong> This website is for general information only and does not replace personalised migration advice. Migration advice is provided by Shamima Yasmin Anwar, Registered Migration Agent (MARN 2619415 · IAA Licence No. 202603837), in accordance with the{" "}
            <a href="/documents/code-of-conduct.pdf" target="_blank" rel="noopener noreferrer" className="text-[#C43A89] underline font-semibold">Migration Agents Code of Conduct</a>.
            For your specific circumstances, please book a paid consultation. We reference official Department of Home Affairs information including visa lists, points tables, the points calculator and SkillSelect guidance.
          </p>
          <div className="flex flex-col md:flex-row justify-between gap-3 pt-4 border-t border-white/10">
            <div>© {new Date().getFullYear()} Shamima Yasmin Anwar. All rights reserved.</div>
            <div className="flex gap-5 flex-wrap">
              <Link to="/privacy-policy" data-testid="footer-privacy-policy" className="hover:text-[#C43A89]">Privacy Policy</Link>
              <a href="/documents/code-of-conduct.pdf" target="_blank" rel="noopener noreferrer" data-testid="footer-code-of-conduct" className="hover:text-[#C43A89]">Code of Conduct</a>
              <Link to="/admin/login" data-testid="footer-admin-link" className="hover:text-[#C43A89]">Admin</Link>
              <a href="https://immi.homeaffairs.gov.au" target="_blank" rel="noopener noreferrer" className="hover:text-[#C43A89]">Home Affairs</a>
              <a href="https://www.mara.gov.au" target="_blank" rel="noopener noreferrer" className="hover:text-[#C43A89]">MARA</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
    </>
  );
}
